import { FC, Ref, useRef } from "react";
import { useBoolean } from "usehooks-ts";

import { COURSE_GROUPING_ID_QUERY_PARAM } from "components/constants";
import { SidebarOptionsMenu, SidebarOptionsMenuProps } from "components/menus/SidebarOptionsMenu";
import { CourseGroupingType } from "components/server-types";
import { t } from "i18n/i18n";
import { ConfirmDeletionDialog } from "mds/components/ConfirmDeletionDialog";
import { IconText } from "mds/components/IconText";
import { MenuInstance, MenuItem } from "mds/components/Menu";
import { PencilEditIcon } from "mds/icons";
import { storeApi } from "store/index";
import { selectCoursesByGroupingId } from "store/selectors/section";
import { useHasItemsSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";
import { ADMIN_COURSE_EVENT_PREFIX } from "utils/amplitude";
import { useQueryParamsActions } from "utils/routing";

interface CourseGroupingOptionsMenuProps extends SidebarOptionsMenuProps {
  courseGrouping: Pick<CourseGroupingType, "id" | "title">;
  onRename: () => void;
}

export const CourseGroupingOptionsMenu: FC<CourseGroupingOptionsMenuProps> = ({
  courseGrouping,
  onRename,
  className,
}) => {
  const menuRef: Ref<MenuInstance> = useRef(null);
  const hasCourses = useHasItemsSelector((state) =>
    selectCoursesByGroupingId(state, courseGrouping.id),
  );
  const { deleteQueryParams } = useQueryParamsActions();

  const { value: dialogIsOpen, setTrue: openDialog, setFalse: closeDialog } = useBoolean(false);

  const handleDelete = async () => {
    await storeApi.course_groupings.destroy(courseGrouping.id);

    toastSuccessMessage(t("home.toasts.grouping_deleted"));

    deleteQueryParams([COURSE_GROUPING_ID_QUERY_PARAM]);
  };

  return (
    <>
      <SidebarOptionsMenu
        className={className}
        eventPrefix={ADMIN_COURSE_EVENT_PREFIX}
        instanceRef={menuRef}
        kind="secondary"
        optionsMenuActions={{
          onDelete: openDialog,
          isDeleteDisabled: hasCourses,
          deleteTooltipText: hasCourses
            ? t("sidebar_options_menu.cannot_delete_grouping_with_courses")
            : undefined,
        }}
      >
        <MenuItem onClick={onRename}>
          <IconText
            iconStart={<PencilEditIcon />}
            text={t("sidebar_options_menu.rename_course_grouping")}
          />
        </MenuItem>
      </SidebarOptionsMenu>
      <ConfirmDeletionDialog
        glossaryKey="course_grouping"
        instanceName={courseGrouping.title}
        open={dialogIsOpen}
        onClose={closeDialog}
        onDelete={handleDelete}
      />
    </>
  );
};
